import React, { useState, useEffect } from 'react';
import { Zap, Plus, Trash2, Check, ClipboardList } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, AIOptionButton, PollResults,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DEFAULT_SETTINGS = { correctAnswer: '', timeLimitMs: 30000, pollResults: 'hidden', isLocked: false, allowUpdateResponse: false, description: false, descText: '' };

const DUMMY_DATA = {
    question: 'What is the capital city of Australia? 🇦🇺',
    options: [
        { id: 'opt1', label: 'Sydney' },
        { id: 'opt2', label: 'Melbourne' },
        { id: 'opt3', label: 'Canberra' },
        { id: 'opt4', label: 'Brisbane' },
    ],
    settings: { ...DEFAULT_SETTINGS, correctAnswer: 'opt3', timeLimitMs: 20000, description: true, descText: 'Answer as fast as you can — speed matters! The fastest correct answer wins.' },
};

const SpeedQuiz = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [options, setOptions] = useState([
        { id: 'opt1', label: 'Option A' },
        { id: 'opt2', label: 'Option B' },
        { id: 'opt3', label: 'Option C' },
        { id: 'opt4', label: 'Option D' },
    ]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [isAddingOptions, setIsAddingOptions] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (Array.isArray(content.options) && content.options.length) setOptions(content.options);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const buildContent = () => ({ question, options, settings });
    const validate = () => !question.trim() ? 'Please enter a question.' : (options.some(o => !o.label.trim()) ? 'All options need labels.' : null);
    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));
    const addOption = () => setOptions(prev => [...prev, { id: `opt${Date.now()}`, label: '' }]);
    const removeOption = async (id) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This quiz already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them chose this option, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(prev => prev.filter(o => o.id !== id));
    };
    const updateOption = (id, val) => setOptions(prev => prev.map(o => o.id === id ? { ...o, label: val } : o));

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const handleAddOptionsWithAI = async () => {
        setIsAddingOptions(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'options', context: { question } }) });
            if (res.ok) { const data = await res.json(); if (data.suggestions) { setOptions(prev => [...prev, ...data.suggestions.map(s => ({ id: `opt${Date.now()}_${Math.random().toString(36).slice(2)}`, label: s }))]); } } else { alert('Failed to generate options. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to generate options. Please try again.'); } finally { setIsAddingOptions(false); }
    };

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ ...activity, content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Type your speed quiz question here..." autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'Type your speed quiz question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Options</span>
                            <span className={styles.markCorrectHint}>Click the badge to mark correct</span>
                        </div>
                        {options.map((opt, i) => {
                            const isCorrect = settings.correctAnswer === opt.id;
                            const letter = String.fromCharCode(65 + i);
                            return (
                                <div key={opt.id} className={`${styles.optionRow} ${isCorrect ? styles.optionRowCorrect : ''}`}>
                                    <Tooltip title={isCorrect ? 'Unmark correct' : 'Mark as correct'} placement="top">
                                        <button className={`${styles.optionLetterBtn} ${isCorrect ? styles.optionLetterBtnCorrect : ''}`} onClick={() => updateSetting('correctAnswer', isCorrect ? '' : opt.id)} aria-label={isCorrect ? `Unmark ${letter}` : `Mark ${letter} as correct`}>
                                            {isCorrect ? <Check size={11} /> : letter}
                                        </button>
                                    </Tooltip>
                                    <input className={`${styles.optionInput} ${isCorrect ? styles.optionInputCorrect : ''}`} value={opt.label} onChange={(e) => updateOption(opt.id, e.target.value)} placeholder={`Option ${letter}`} />
                                    {isCorrect && <span className={styles.correctBadge}><Check size={10} /> Correct</span>}
                                    {options.length > 2 && <button className={styles.optionRemoveBtn} onClick={() => removeOption(opt.id)} aria-label="Remove option"><Trash2 size={13} /></button>}
                                </div>
                            );
                        })}
                        <div className={styles.optionsFooter}>
                            <button className={styles.addOptionBtn} onClick={addOption}><Plus size={14} /> Add option</button>
                            <AIOptionButton onClick={handleAddOptionsWithAI} loading={isAddingOptions} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Time limit" description="How long participants have to answer">
                <select value={settings.timeLimitMs} onChange={e => updateSetting('timeLimitMs', Number(e.target.value))} style={{ padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>
                    {[5000, 10000, 15000, 20000, 30000, 60000].map(ms => <option key={ms} value={ms}>{ms / 1000}s</option>)}
                </select>
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<Zap size={20} />} color="var(--cat-rose)"
            title="Speed Quiz" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default SpeedQuiz;
